<template>
  <v-app>
    <v-container fluid>
      <!-- ================== SEARCH PANEL ================== -->
      <v-expansion-panels v-model="panel" focusable>
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Search Form
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-form @submit.prevent="submit">
              <v-row class="px-2 pt-4 pb-0" dense>
                <v-col lg="3" md="4" cols="12">
                  <v-autocomplete
                    label="Customer"
                    variant="outlined"
                    density="compact"
                    bg-color="grey-lighten-4"
                    v-model="customer_id.value.value"
                    :items="getCustomerMapList"
                    :item-title="(item) => item ? `${item.customer.company_name || item.customer.company_owner_name || ''} - ${item.customer.code}` : ''"
                    item-value="customer_id"
                    :error-messages="customer_id.errorMessage.value"
                    clearable
                  ></v-autocomplete>
                </v-col>

                <v-col lg="3" md="4" cols="12">
                  <v-text-field
                    label="Invoice Code"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="invoice_code.value.value"
                    :error-messages="invoice_code.errorMessage.value"
                    clearable
                  ></v-text-field>
                </v-col>

                <v-col lg="2" md="4" cols="12">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    label="From Date"
                    density="compact"
                    single-line
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar-month"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="from_date.value.value"
                    :error-messages="from_date.errorMessage.value"
                    clearable
                  ></v-date-input>
                </v-col>

                <v-col lg="2" md="4" cols="12">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    label="To Date"
                    density="compact"
                    single-line
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar-month"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="to_date.value.value"
                    :error-messages="to_date.errorMessage.value"
                    clearable
                  ></v-date-input>
                </v-col>

                <v-col lg="2" md="4" cols="12" class="d-flex align-center">
                  <v-btn class="bg-brand" type="submit" ref="searchButton">Search</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- ================== REPORT PANEL ================== -->
    <v-container fluid style="margin-top: -20px;">
      <v-expansion-panels v-model="panelReport" focusable>
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Sales Statement Reports
          </v-expansion-panel-title>
          <v-expansion-panel-text>

            <!-- ================== Filters + Export ================== -->
            <v-row class="py-2" align="center">
              <v-col md="4" cols="12">
                <v-text-field
                  v-model="search"
                  append-inner-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  clearable
                ></v-text-field>
              </v-col>

              <v-col md="5" cols="12">
                <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">CSV</v-btn>
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">EXCEL</v-btn>
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">PDF</v-btn>
                </v-btn-group>
              </v-col>
            </v-row>

            <!-- ================== Tabs for Reports ================== -->
            <v-tabs v-model="activeTab" grow>
              <v-tab value="flat">Flat Table</v-tab>
              <v-tab value="grouped">Grouped</v-tab>
              <v-tab value="summary">Seller Summary</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="mt-2">

              <!-- ========== FLAT TABLE ========= -->
              <v-tab-item value="flat">
                <v-data-table
                  :headers="flatHeaders"
                  :items="flatRows"
                  :search="search"
                  dense
                  class="elevation-1"
                  :items-per-page="-1"
                  hide-default-footer
                >
                  <template v-slot:item.index="{ index }">{{ ++index }}</template>
                </v-data-table>
              </v-tab-item>

              <!-- ========== GROUPED REPORT ========= -->
              <v-tab-item value="grouped">
                <v-accordion>
                  <v-accordion-item
                    v-for="(projects, seller) in groupedData"
                    :key="seller"
                    :title="seller"
                  >
                    <v-list>
                      <v-list-item
                        v-for="project in projects"
                        :key="project.id"
                      >
                        <v-list-item-content>
                          <strong>{{ project.name }} ({{ project.code }})</strong>
                          <v-list dense>
                            <v-list-item
                              v-for="author in project.sales_orders"
                              :key="author.id"
                            >
                              <v-list-item-content>
                                {{ author.author_name }} - Agreed: {{ author.agreed_amount }}, Paid: {{ author.paid_amount }}, Due: {{ author.due_amount }}
                                <v-list dense>
                                  <v-list-item
                                    v-for="p in author.payments"
                                    :key="p.id"
                                  >
                                    Payment: {{ p.amount }} | Date: {{ p.payment_date || 'N/A' }}
                                  </v-list-item>
                                </v-list>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-accordion-item>
                </v-accordion>
              </v-tab-item>

              <!-- ========== SELLER SUMMARY ========= -->
              <v-tab-item value="summary">
                <v-data-table
                  :headers="summaryHeaders"
                  :items="summaryRows"
                  dense
                  class="elevation-1"
                  :search="search"
                  :items-per-page="-1"
                  hide-default-footer
                ></v-data-table>
              </v-tab-item>

            </v-tabs-items>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import Axios from "axios";
import formattedDate from "@/plugins/dateFormatter";
import { getSalesStatementReports } from "@/store/api.js";

const panel = ref([0]);
const panelReport = ref([0]);
const activeTab = ref("flat");
const search = ref("");

// --------------------- Form fields ---------------------
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable().label("From Date"),
    to_date: yup.date().nullable().label("To Date"),
  }),
});
const from_date = useField("from_date");
const to_date = useField("to_date");
const customer_id = useField("customer_id");
const invoice_code = useField("invoice_code");

const store = useStore();
const reportData = ref([]);
const summary = ref({});
const filters = ref({ with_details: false });

// --------------------- Customer list ---------------------
const getCustomerMapList = computed(() => store.getters.getCustomerMapList || []);

// --------------------- Submit Function ---------------------
const submit = handleSubmit(async (values) => {
  const from = formattedDate(from_date.value.value);
  const to = formattedDate(to_date.value.value);

  filters.value = {
    customer_id: customer_id.value.value,
    invoice_code: invoice_code.value.value,
    from_date: from,
    to_date: to,
    with_details: filters.value.with_details,
  };

  try {
    const { data } = await Axios.post(getSalesStatementReports, filters.value);
    reportData.value = data.data;
    summary.value = data.summary;
  } catch (err) {
    console.error(err);
  }
});

// --------------------- OPTION A: Flat Table ---------------------
const flatRows = computed(() => {
  const rows = [];
  reportData.value.forEach(project => {
    project.sales_orders?.forEach(order => {
      const paymentTotal = order.payments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
      rows.push({
        seller: project.contacted_by?.name,
        project: project.name,
        author: order.author_name,
        agreed: order.agreed_amount,
        paid: paymentTotal.toFixed(2),
        due: order.due_amount,
        payment_count: order.payments?.length || 0
      });
    });
  });
  return rows;
});
const flatHeaders = [
  { title: "#SL", key: "index", align: "center" },
  { title: "Seller", key: "seller" },
  { title: "Project", key: "project" },
  { title: "Author", key: "author" },
  { title: "Agreed Amount", key: "agreed" },
  { title: "Paid Amount", key: "paid" },
  { title: "Due Amount", key: "due" },
  { title: "Payment Count", key: "payment_count" }
];

// --------------------- OPTION B: Grouped ---------------------
const groupedData = computed(() => {
  const grouped = {};
  reportData.value.forEach(project => {
    const seller = project.contacted_by?.name || "Unknown Seller";
    if (!grouped[seller]) grouped[seller] = [];
    grouped[seller].push(project);
  });
  return grouped;
});

// --------------------- OPTION C: Seller Summary ---------------------
const summaryRows = computed(() => {
  const sellers = {};
  reportData.value.forEach(project => {
    const seller = project.contacted_by?.name || "Unknown Seller";
    if (!sellers[seller]) {
      sellers[seller] = {
        seller,
        total_projects: 0,
        total_authors: 0,
        total_agreed: 0,
        total_paid: 0,
        total_due: 0
      };
    }
    sellers[seller].total_projects += 1;
    project.sales_orders?.forEach(order => {
      sellers[seller].total_authors += 1;
      sellers[seller].total_agreed += parseFloat(order.agreed_amount || 0);
      sellers[seller].total_paid += parseFloat(order.paid_amount || 0);
      sellers[seller].total_due += parseFloat(order.due_amount || 0);
    });
  });
  return Object.values(sellers);
});
const summaryHeaders = [
  { title: "Seller", key: "seller" },
  { title: "Total Projects", key: "total_projects" },
  { title: "Total Authors", key: "total_authors" },
  { title: "Total Agreed", key: "total_agreed" },
  { title: "Total Paid", key: "total_paid" },
  { title: "Total Due", key: "total_due" }
];

// --------------------- Export Functions (Stub, can implement real) ---------------------
function exportToCSV() { console.log("Export CSV"); }
function exportToExcel() { console.log("Export Excel"); }
function exportToPDF() { console.log("Export PDF"); }

</script>
